import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/CompanyContext';
import { trackMyResultsViewed } from '@/utils/amplitudeEvents';
import { Brain, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AssessmentHistoryCard from '@/components/results/AssessmentHistoryCard';

const MyResults: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentCompany } = useCompany();

  const { data: submissions, isLoading } = useQuery({
    queryKey: ['my-results', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('survey_submissions')
        .select('*')
        .eq('user_id', user.id)
        .order('completed_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  // EVENT 17: My Results Viewed (with guard to prevent duplicate fires)
  const hasTrackedMyResults = React.useRef(false);
  useEffect(() => {
    if (!isLoading && submissions && !hasTrackedMyResults.current) {
      hasTrackedMyResults.current = true;
      trackMyResultsViewed({
        company_id: currentCompany?.company_id || null,
        user_id: user?.id || null,
        assessments_completed_count: submissions.length,
      });
    }
  }, [isLoading, submissions?.length]);

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-background border-b border-border sticky top-0 z-50">
        <div className="tesla-container py-4 md:py-6 flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/')}
          >
            <Brain className="h-7 w-7 md:h-8 md:w-8 text-foreground" />
            <span className="text-lg md:text-xl font-bold text-foreground tracking-tight">
              Zentrix Insights
            </span>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>
      </nav>

      <main className="tesla-container py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-black mb-12">My Results</h1>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-black border-t-transparent rounded-full" />
            </div>
          ) : submissions && submissions.length > 0 ? (
            <div className="space-y-8">
              {submissions.map((submission) => (
                <AssessmentHistoryCard
                  key={submission.id}
                  submission={submission}
                  onView={() => navigate(`/results/${submission.id}`)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
              <h2 className="text-xl font-semibold text-black mb-4">No Results Yet</h2>
              <p className="text-gray-600 mb-8">
                You haven't completed any assessments yet. Start your first one now!
              </p>
              <Button onClick={() => navigate('/assessment')} className="px-12 py-6 bg-black hover:bg-black/90 text-white font-bold text-lg">
                Take Assessment
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default MyResults;
